import React from 'react';
import {
  Check,
  Clock,
  Handshake,
  Lock,
  BellOff,
  CalendarCheck,
  CalendarClock,
  Coffee,
  Phone,
  UserCheck,
  UserRoundCheck,
  Users,
  Utensils,
  Zap,
} from 'lucide-react';

import {
  fullLeadName,
  sourceIdentity,
  currentStepOf,
  stepsForToday,
} from './boardUtils';
import {
  intakeStartOf,
  intakeReleaseOf,
  intakeNotStarted,
  startsAtText,
  stepTone,
  stepCountdownLabel,
  effectiveStepWindow,
  stepPausedKindNow,
  countdownLabel,
  fillFor,
  AWAITING_LOG_TTL_MS,
  STRIPE,
  INTAKE_DEADLINE_MS,
  pauseKindAt,
  livePauseAt,
} from './slaUtils';

// The "away" mark (Nic 2026-10-05), one rule everywhere: fork and knife for
// lunch, a calendar for a meeting, a coffee cup for a break, a phone for a
// call (2026-11-02). No kind (time added by any mix of them) = the coffee
// cup. In the text's own color, so it sits in white on a solid tile and
// muted on a light one.
const AWAY_ICON = {
  lunch: Utensils,
  meeting: CalendarClock,
  away: Coffee,
  call: Phone,
};
// One wording per pause kind, shared by the new-lead and run tiles.
export const pausedLabelOf = (kind) =>
  kind === 'lunch'
    ? 'paused for lunch'
    : kind === 'meeting'
      ? 'paused, in a meeting'
      : kind === 'call'
        ? 'paused, on a call'
        : 'paused, on a break';
export const AwayGlyph = ({ kind = null, size = 12, className = '' }) => {
  const Icon = AWAY_ICON[kind] || Coffee;
  return (
    <Icon
      aria-hidden
      size={size}
      strokeWidth={2.25}
      className={`shrink-0 ${className}`}
    />
  );
};

// Per-user tile style ('classic' stripe cards vs 'solid' tone-filled cards),
// provided by BoardPage from the server-saved board prefs. A context instead
// of prop drilling: tiles render through BoardColumn, which has
// no business knowing about display preferences.
export const TileStyleContext = React.createContext('classic');
const useSolidTiles = () => React.useContext(TileStyleContext) === 'solid';

// The org work calendar, provided once by BoardPage from the board payload.
// A context rather than a prop because it is genuinely global (one calendar
// for the whole org, identical for every tile) and would otherwise have to be
// drilled through every column and row to reach the step-closing math.
export const WorkCalendarContext = React.createContext(null);
export const useWorkCalendar = () => React.useContext(WorkCalendarContext);

// Both board-wide contexts in one wrapper. Purely so adding the second one
// did not re-indent BoardPage's entire JSX tree and bury a 47-line change in
// 700 lines of whitespace.
export const BoardContexts = ({ tileStyle, workCalendar, children }) => (
  <TileStyleContext.Provider value={tileStyle}>
    <WorkCalendarContext.Provider value={workCalendar}>
      {children}
    </WorkCalendarContext.Provider>
  </TileStyleContext.Provider>
);

const clockLabel = (d) =>
  new Date(d).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });


// A future-day step reads as a schedule, not a countdown: "Day 2 · 7:00 AM"
// beats "in 8h 34m".
const nextStepLabel = (step, run, now) => {
  if (step.day > Math.min(run.currentDay, 10)) {
    return `Day ${step.day} · ${clockLabel(step.windowStartAt)}`;
  }
  return `opens ${countdownLabel(step.windowStartAt, now)}`;
};

// The left-stripe card shell. Bootstrap's global `.border` carries !important
// and flattens side-specific widths, so the stripe is declared with an
// arbitrary border-width utility + inline colors (learned the hard way).
// `halo` = the breathing amber glow for "the window is open RIGHT NOW".
const PULSE_PERIOD_MS = 3600; // keep in step with os-ring-breathe in the css

const TileShell = ({
  stripe,
  onClick,
  children,
  className = '',
  halo = false,
}) => {
  // Solid style: the whole tile wears the tone (deeper fill, white text) and
  // the stripe/halo disappear — an amber breathing outline cannot read on an
  // amber fill, and the fill itself is the urgency signal.
  const solid = useSolidTiles();
  const useHalo = halo && !solid;
  // Sync every halo to a shared wall-clock grid so ALL due outlines fade in
  // and out TOGETHER, board-wide. The negative delay must be captured at the
  // moment the class is APPLIED (not at mount — a tile that becomes due
  // later, e.g. right after Take, would otherwise start off-phase).
  const phaseRef = React.useRef(null);
  if (useHalo && phaseRef.current === null) {
    phaseRef.current = `-${Date.now() % PULSE_PERIOD_MS}ms`;
  } else if (!useHalo) {
    phaseRef.current = null;
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className={`min-w-0 overflow-hidden rounded-lg text-left shadow-[0_1px_2px_rgba(15,23,42,0.04),0_2px_8px_rgba(15,23,42,0.06)] transition-shadow hover:shadow-[0_2px_4px_rgba(15,23,42,0.06),0_8px_20px_rgba(15,23,42,0.09)] focus:outline-none focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-500/40 ${
        solid
          ? 'text-white'
          : 'bg-white border-solid [border-width:0_0_0_4px] dark:!bg-surface-card'
      } ${useHalo ? 'os-pulse' : ''} ${className}`}
      style={{
        ...(solid
          ? { background: fillFor(stripe) }
          : { borderLeftColor: stripe }),
        ...(phaseRef.current ? { animationDelay: phaseRef.current } : {}),
      }}
    >
      {children}
    </button>
  );
};

// Window burn-down: fills as the open window elapses — "how much window is
// left" without reading the countdown. Amber the whole way: in the window
// is yellow by definition; red is reserved for actually behind.
const BurnDownBar = ({ pct }) => {
  const solid = useSolidTiles();
  return (
    <div
      className={`mt-1.5 h-1 w-full overflow-hidden rounded-full ${
        solid ? 'bg-white/25' : 'bg-slate-100 dark:bg-white/10'
      }`}
    >
      <div
        className={`h-full rounded-full transition-[width] duration-1000 ${
          solid ? 'bg-white' : 'bg-amber-400'
        }`}
        style={{ width: `${Math.round(Math.min(pct, 1) * 100)}%` }}
      />
    </div>
  );
};

// The reach-out badge is a durable FACT ("this person contacted us and RC
// caught it"), not an alert: it keys off SYSTEM-DETECTED inbound touches
// only (Nic 2026-08-10 — a human's Contact Made click also logs a touch,
// but the state move is that signal), so dismissing the slide-over's
// detection prompt never clears it. One unified icon for calls and texts
// alike (Nic 2026-08-05) — the channel detail lives in the panel.
const Badges = ({
  lead,
  reachOuts,
  fill,
  booking,
  observedAt,
  // The «STOP» · Confirm drop pill carries its own BellOff — two bells on
  // one row is noise, so the parked tile suppresses this quiet one.
  suppressOptOut = false,
  // "Existing account" (2026-08-25, visibility-only v1). Defaults from the
  // lead, but intake tiles pass lead={null}, so they hand it in explicitly.
  existingAccount = null,
}) => {
  const solid = useSolidTiles();
  const account = existingAccount || lead?.existingAccount || null;
  const reachOutCount = (reachOuts || []).length;
  // A live Calendly booking is a fact worth a chip; a canceled/no-show one
  // recedes to muted amber (the panel carries the detail).
  const bookingLive = booking && booking.status === 'active';
  const bookingLabel = booking
    ? new Date(booking.startTime).toLocaleString([], {
        weekday: 'short',
        hour: 'numeric',
        minute: '2-digit',
      })
    : null;
  return (
    // min-w-0 (not shrink-0, 2026-08-25): the Existing Account chip is the
    // one wide badge and truncates itself; every other chip keeps its
    // intrinsic width (no min-w-0), so nothing else squishes.
    <span className="flex min-w-0 items-center gap-1">
      {account ? (
        <span
          className={`inline-flex min-w-0 items-center gap-1 rounded px-1.5 py-px text-[10px] font-semibold ${
            solid
              ? 'bg-white text-amber-700'
              : 'bg-amber-50 text-amber-600 dark:bg-amber-500/15 dark:text-amber-400'
          }`}
          // Solid tiles tint the pill text with the tile color when the call
          // site provides one; intake tiles pass no fill, so the amber
          // class above is the readable fallback (white-on-white otherwise).
          style={solid && fill ? { color: fill } : undefined}
          title={`Existing account: ${account.name} (${
            account.userType === 'pending_client' ? 'pending client' : 'client'
          }), matched by ${account.matchedBy}. This person likely signed up on their own.`}
        >
          <UserRoundCheck size={13} className="shrink-0" />
          <span className="truncate">Existing Account</span>
        </span>
      ) : null}
      {lead?.leadSmsOptOutAt && !suppressOptOut ? (
        <span title="Texted STOP, SMS suppressed">
          <BellOff
            size={13}
            strokeWidth={2.6}
            className={
              solid ? 'text-white/80' : 'text-slate-400 dark:text-slate-500'
            }
          />
        </span>
      ) : null}
      {booking ? (
        <span
          className={`inline-flex items-center gap-1 rounded px-1.5 py-px text-[10px] font-semibold ${
            solid
              ? 'bg-white'
              : bookingLive
                ? 'bg-emerald-50 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-400'
                : 'bg-amber-50 text-amber-600 dark:bg-amber-500/15 dark:text-amber-400'
          }`}
          style={solid ? { color: fill } : undefined}
          title={`${booking.eventTypeName || 'Meeting'}${booking.hostName ? ` with ${booking.hostName}` : ''}${bookingLive ? '' : booking.status === 'canceled' ? ' (canceled)' : ' (no-show)'}`}
        >
          <CalendarCheck size={13} />
          {bookingLabel}
        </span>
      ) : null}
      {reachOutCount > 0 ? (
        <span
          className={`inline-flex items-center gap-1 rounded px-1.5 py-px text-[10px] font-semibold ${
            solid
              ? 'bg-white'
              : 'bg-emerald-50 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-400'
          }`}
          style={solid ? { color: fill } : undefined}
          title={`Has reached out ${reachOutCount} time${reachOutCount > 1 ? 's' : ''} from this number`}
        >
          <Handshake size={13} />
          {reachOutCount}
        </span>
      ) : null}
      {observedAt ? (
        // Quiet fact, not an alert: contact happened on a call but the
        // specialist chose "Not yet" — the run is still working. Deliberately
        // NOT the handshake (that stays RC-detected reach-outs only).
        <span
          title={`Contact was made on a call (${new Date(observedAt).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}), not confirmed yet`}
        >
          <UserCheck
            size={13}
            strokeWidth={2.6}
            className={
              solid ? 'text-white/80' : 'text-slate-400 dark:text-slate-500'
            }
          />
        </span>
      ) : null}
    </span>
  );
};

// ---------------------------------------------------------------------------
// Top-lane tiles: compact, horizontal-flow cards.
// ---------------------------------------------------------------------------

// Intake lane tile: the speed-to-take clock + 1-click Take.
// Channel-tinted source chip: one glance says HOW this lead arrived.
// Organizations (Envoy + org partners) = purple, individual partners = teal,
// non-partner referrers = sky (upgrades to teal retroactively if they become
// a partner), event = amber (own tab; chip exists for safety).
const SOURCE_CHIP_TONE = {
  org: 'bg-violet-50 text-violet-600 dark:bg-violet-500/15 dark:text-violet-300',
  partner: 'bg-teal-50 text-teal-700 dark:bg-teal-500/15 dark:text-teal-300',
  person: 'bg-sky-50 text-sky-600 dark:bg-sky-500/15 dark:text-sky-300',
  event: 'bg-amber-50 text-amber-600 dark:bg-amber-500/15 dark:text-amber-300',
  other: 'bg-slate-100 text-slate-500 dark:bg-white/10 dark:text-slate-400',
};

export const SourceChip = ({ lead, className = '' }) => {
  const id = sourceIdentity(lead);
  if (!id.label) return null;
  return (
    <span
      className={`inline-flex min-w-0 items-center rounded px-1.5 py-px text-[10px] font-semibold ${SOURCE_CHIP_TONE[id.channel] || SOURCE_CHIP_TONE.other} ${className}`}
      title={
        id.detail && id.detail !== id.label
          ? `${id.label} · ${id.detail}`
          : id.label
      }
    >
      <span className="truncate">{id.label}</span>
    </span>
  );
};

// A new lead's take window as a step: the same 0-3 minute window Day 1's
// Call #1 runs, opening at the release. Exported so the server's metric
// (onboardingMetricsController) and this tile walk the identical shape.
export const intakeWindowOf = (releaseAt) => ({
  windowStartAt: new Date(releaseAt),
  windowEndAt: new Date(new Date(releaseAt).getTime() + INTAKE_DEADLINE_MS),
  status: 'pending',
  meta: {},
});

// An Intake lead's clock (Nic 2026-10-21: foolproof). `pauses` is the
// owner's lunch, meetings and breaks (one interval or a list, ownerPausesOf),
// null for a communal lead. The window runs through the SAME pause math as
// the taken tiles (effectiveStepWindow), so the countdown stands still for
// ANY time its owner is away, not just lunch. A lead that landed while its
// owner was away is held by the server's release (computeIntakeReleases):
// blue "opens in" until they're back, then the normal 3 minutes.
export const intakeClockOf = (lead, now, pauses) => {
  // The clock start, not the arrival (Nic 2026-08-15): a lead that landed
  // after hours or was pushed is not late, it has not begun.
  const startsAt = intakeStartOf(lead);
  const notStarted = intakeNotStarted(lead, now);
  // The RELEASE, not the raw clock start (Nic 2026-08-21): a lead that
  // landed during lunch, a meeting, a break or after hours waits its turn.
  const releaseAt = intakeReleaseOf(lead);
  const window = intakeWindowOf(releaseAt);
  const eff = effectiveStepWindow(window, pauses, now);
  const t = now.getTime();
  // Its owner is away right now (lunch, a meeting, a break, a call)? A lone
  // lunch interval carries no kind.
  const ownerAwayKind = pauseKindAt(pauses, now);
  // 'lunch' | 'meeting' | 'away' while it is frozen right now: inside its
  // 3 minutes, or still waiting on an owner who is away (Nic 2026-10-21: a
  // lead that landed on someone away just reads "paused" until they're
  // back, then its countdown starts as if it had just come in).
  const pausedKind = notStarted
    ? null
    : stepPausedKindNow(window, pauses, now) ||
      (t < eff.startAt.getTime() && ownerAwayKind ? ownerAwayKind : null);
  const paused = !!pausedKind;
  const state =
    notStarted || t < eff.startAt.getTime()
      ? 'waiting'
      : t <= eff.endAt.getTime()
        ? 'due'
        : 'overdue';
  const tone = {
    stripe: state === 'waiting' ? STRIPE.upcoming : state === 'due' ? STRIPE.due : STRIPE.overdue,
    state,
    hot: state === 'overdue',
  };
  const left = countdownLabel(eff.endAt, now);
  const label = notStarted
    ? `Starts ${startsAtText(releaseAt, now)}`
    : paused
      ? pausedLabelOf(pausedKind)
      : state === 'waiting'
        ? `opens ${countdownLabel(eff.startAt, now)}`
        : left.startsWith('in ')
          ? `${left.slice(3)} left`
          : left;
  return { paused, pausedKind, startsAt, notStarted, releaseAt, tone, label };
};

// A small mark on the new-lead tile: an icon, the words in its hover, so it
// never covers the name (Nic 2026-10-21).
const ChipMark = ({ title, children }) => (
  <span role="img" title={title} aria-label={title} className="inline-flex shrink-0 items-center">
    {children}
  </span>
);

// A COMMUNAL new lead at the top of Day 1 (nobody on the roster, the rare
// fallback; an assigned lead's run starts on its own and is a RunTile). Same clock as before: Call #1's 0-3 minute
// window from the release. BLUE (STRIPE.upcoming) while it waits ("opens
// in 20m" / "Starts 8:00 AM"), AMBER once its 3 minutes run, RED once late.
// The clock stands still for any time its owner is away (intakeClockOf).
// It follows the board's tile style (Classic: white with the color on the
// left edge; Solid: filled). The name first; the marks are icons with their
// words in the hover, so nothing covers the name. No buttons: the tile opens
// the slide-over, where Take and Push are, so the rep sees who they are
// before taking. Once taken it becomes a normal Day 1 tile.
export const NewLeadTile = ({ lead, now, onOpen, pauses = null }) => {
  const solid = useSolidTiles();
  const { paused, pausedKind, tone, label } = intakeClockOf(lead, now, pauses);
  const waiting = tone.state === 'waiting';
  const late = tone.state === 'overdue';
  // Same colors as a new lead's run tile: blue while it waits (no pink since
  // 2026-10-23, Day 1's New section marks them), yellow while its 3 minutes
  // run, red once late.
  const stripe = waiting ? STRIPE.upcoming : late ? STRIPE.overdue : STRIPE.due;
  const textTone = waiting
    ? 'text-sky-600 dark:text-sky-300'
    : late
      ? 'text-red-500 dark:text-red-400'
      : 'text-amber-600 dark:text-amber-400';
  const account = lead.existingAccount;
  const icon = (cls) => (solid ? 'text-white/90' : cls);
  return (
    <button
      type="button"
      data-testid="new-lead-tile"
      onClick={() => onOpen(lead)}
      aria-label={`New lead ${fullLeadName(lead)}, ${label}`}
      style={solid ? { background: fillFor(stripe) } : { borderLeftColor: stripe }}
      className={`block w-full rounded-lg px-3 py-2 text-left shadow-[0_1px_2px_rgba(15,23,42,0.04),0_2px_8px_rgba(15,23,42,0.06)] outline-none transition-[transform,box-shadow] duration-150 hover:-translate-y-px hover:shadow-[0_2px_4px_rgba(15,23,42,0.06),0_8px_20px_rgba(15,23,42,0.1)] focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60 motion-reduce:transition-none motion-reduce:hover:translate-y-0 ${
        solid ? 'text-white' : 'border-solid bg-white [border-width:0_0_0_4px] dark:!bg-surface-card'
      }`}
    >
      <span className="flex min-w-0 items-center gap-1">
        <span className={`truncate text-sm font-semibold ${solid ? 'text-white' : 'text-slate-800 dark:text-slate-100'}`}>
          {fullLeadName(lead)}
        </span>
        {account ? (
          <ChipMark title={`Existing account: ${account.name}, matched by ${account.matchedBy}. They likely signed up on their own.`}>
            <UserRoundCheck size={13} className={icon('text-amber-500 dark:text-amber-400')} />
          </ChipMark>
        ) : null}
        {lead.booking ? (
          <ChipMark title={`${lead.booking.eventTypeName || 'Meeting'} booked`}>
            <CalendarCheck size={13} className={icon('text-emerald-500 dark:text-emerald-400')} />
          </ChipMark>
        ) : null}
        {!lead.intakeAssignedTo ? (
          <ChipMark title="Communal: nobody was assigned. Anyone can take them.">
            <Users size={13} className={icon('text-slate-400')} />
          </ChipMark>
        ) : !lead.isMine ? (
          <ChipMark title={`Assigned to ${lead.intakeAssignedToName || 'a specialist'}`}>
            <span className={`rounded-full px-1 text-[9.5px] font-bold ${solid ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-500 dark:bg-white/10 dark:text-slate-300'}`}>
              {(lead.intakeAssignedToName || '?').split(/\s+/).map((w) => w[0]).join('').toUpperCase().slice(0, 2)}
            </span>
          </ChipMark>
        ) : null}
      </span>
      <span className="mt-0.5 flex items-center justify-between gap-2 text-[11px] font-semibold">
        <span className={solid ? 'text-white/90' : textTone}>New lead</span>
        <span className={`inline-flex shrink-0 items-center gap-1 ${solid ? 'text-white' : textTone}`}>
          {paused ? <AwayGlyph kind={pausedKind} size={11} /> : waiting ? null : <Clock size={11} />}
          <span className="whitespace-nowrap">{label}</span>
        </span>
      </span>
    </button>
  );
};

// Checkbox squares replaced the color-dot language (Nic 2026-08-06): the
// tile's stripe/fill + countdown already carry urgency, so the squares carry
// PROGRESS — one box per step scheduled today, a check when it's done.
// "2 of 3 done" reads instantly; per-step detail lives in the slide-over.
// Done keeps the board's one completion color (emerald); everything not yet
// done (pending, due, behind, chain-locked alike) is the same quiet empty
// box; skipped fades so the day's total still adds up without implying work
// owed.
const StepChecks = ({ run, fill }) => {
  const solid = useSolidTiles();
  const todays = stepsForToday(run);
  if (todays.length === 0) return null;
  const boxFor = (s) => {
    if (s.status === 'done') return solid ? 'bg-white' : 'bg-emerald-500';
    if (s.status.startsWith('skipped') || s.status === 'cancelled')
      return solid
        ? 'bg-white/25 opacity-60'
        : 'border border-slate-200 bg-slate-100 opacity-60 dark:!border-white/10 dark:bg-white/10';
    return solid
      ? 'border border-white/50 bg-white/10'
      : 'border border-slate-300 bg-white dark:!border-slate-500 dark:!bg-transparent';
  };
  return (
    <div className="mt-1.5 flex items-center gap-1">
      {todays.map((s) => (
        <span
          key={s.cadenceStepId}
          title={`${s.label || s.stepKey} · ${
            s.status === 'skipped_no_email'
              ? 'skipped, no email on record'
              : s.status === 'skipped_optout'
                ? 'opted out'
                : s.status === 'skipped_condition'
                  ? 'skipped'
                  : s.status
          }`}
          className={`flex h-3.5 w-3.5 items-center justify-center rounded-[4px] ${boxFor(s)}`}
        >
          {s.status === 'done' ? (
            <Check
              size={10}
              strokeWidth={3.5}
              className={solid ? '' : 'text-white'}
              style={solid ? { color: fill } : undefined}
            />
          ) : null}
        </span>
      ))}
    </div>
  );
};

export const RunTile = ({
  run,
  now: liveNow,
  onOpen,
  cadenceVersion,
  ownerLunch = null,
  // Show whose lead it is (managers, admins, view only).
  showOwner = false,
}) => {
  const solid = useSolidTiles();
  const workCalendar = useWorkCalendar();
  const step = currentStepOf(run, cadenceVersion, workCalendar, liveNow);
  // Call in progress (broadcast by whoever is dialing): the tile FREEZES at
  // the dial instant — a yellow tile 10 seconds from red stays yellow for
  // the whole call instead of silently burning while the work is happening.
  const onCall = !!run.activeCall;
  // Awaiting log (Nic 2026-08-24, Austin's first lead): the call HUNG UP but
  // the outcome isn't clicked yet. The dial happened in-window, the final
  // judgment (dial time) will read on time — so the tile must not flash red
  // during the wrap-up seconds. Same freeze, anchored at the placement
  // instant callEnded stamped onto the step; TTL-bounded so a never-logged
  // call eventually resumes the real clock.
  const placedAtIso =
    !onCall && step?.status === 'pending' ? step?.meta?.callPlacedAt : null;
  const placedAt = placedAtIso ? new Date(placedAtIso) : null;
  // In-window judged against the EFFECTIVE end (lunch credit stretches
  // windows; the tone below judges by the same end, so the grace must too).
  // Computed with liveNow — the frozen `now` doesn't exist yet.
  const graceEndAt = placedAt
    ? effectiveStepWindow(step, ownerLunch, liveNow).endAt
    : null;
  const awaitingLog =
    !!placedAt &&
    !Number.isNaN(placedAt.getTime()) &&
    placedAt.getTime() <= new Date(graceEndAt).getTime() &&
    liveNow - placedAt < AWAITING_LOG_TTL_MS;
  const now = onCall
    ? new Date(run.activeCall.startedAt)
    : awaitingLog
      ? placedAt
      : liveNow;
  // DERIVED LUNCH PAUSE (Nic 2026-08-11): windows render as authored; the
  // owner's lunch stops the clock instead. All time math runs against the
  // EFFECTIVE window (raw + consumed pause) — during lunch the credit grows
  // 1:1 with now, so the countdown freezes on its own and the tile shows
  // "🥪 paused +Xm" with the credit ticking up; afterwards a "🥪+Xm" marker
  // rides next to the resumed countdown.
  const lunchApplies =
    step &&
    step.status === 'pending' &&
    !(step.meta?.autoSend && !step.meta?.autoSendBlocked);
  const eff = lunchApplies
    ? effectiveStepWindow(step, ownerLunch, now)
    : { credit: 0 };
  // 'lunch' | 'meeting' | null: which pause is freezing the clock now.
  const paused = onCall ? null : stepPausedKindNow(step, ownerLunch, liveNow);
  const creditMin = Math.floor((eff.credit || 0) / 60000);
  const clockStep =
    eff.credit > 0
      ? { ...step, windowStartAt: eff.startAt, windowEndAt: eff.endAt }
      : step;
  const tone = stepTone(clockStep, now);
  const todaySteps = stepsForToday(run);
  const quietDay = todaySteps.length === 0;
  const inWindow = tone.state === 'due';
  // Every step scheduled today is resolved (done or skipped, nothing pending
  // or missed): the tile's story is "finished" — green stripe + check — with
  // tomorrow's plan demoted to a muted footnote instead of masquerading as
  // work to do.
  const dayComplete =
    !quietDay &&
    todaySteps.every((s) => s.status !== 'pending' && s.status !== 'missed');
  // A brand-new lead (Nic 2026-10-21): its run started on its own and
  // nobody has reached out yet (status 'new' until the first attempt). It
  // reads PINK while it waits for Call #1 (its owner away, its 5-minute
  // step, the next morning) instead of blue, then the normal yellow / red;
  // the "New" chip stays until the first touch.
  const isNewLead = run.state === 'active' && run.lead?.leadStatus === 'new';
  const ownerAwayNow = isNewLead ? livePauseAt(ownerLunch, liveNow) : null;
  const heldNew = isNewLead && tone.state === 'upcoming';
  // Locked until its clock starts (Nic 2026-10-25): a small lock by "opens in".
  const lockedNew =
    !!run.startsAt && new Date(run.startsAt) > liveNow && run.state === 'active';
  const tileStripe = dayComplete
    ? STRIPE.done
    : quietDay && tone.state === 'idle'
        ? STRIPE.quiet
        : tone.stripe;
  // In solid mode the tone is the tile; secondary text goes translucent
  // white and the countdown colors collapse to white (the fill says it all).
  const mutedText = solid
    ? 'text-white/75'
    : 'text-slate-400 dark:text-slate-500';

  return (
    <TileShell
      stripe={tileStripe}
      onClick={() => onOpen(run)}
      className="w-full px-2.5 py-2"
      halo={inWindow && !dayComplete}
    >
      <div className="flex items-start justify-between gap-1.5">
        <span
          className={`truncate text-sm font-semibold ${
            solid ? 'text-white' : 'text-slate-800 dark:text-slate-100'
          }`}
        >
          {fullLeadName(run.lead)}
        </span>
        {/* min-w-0 + flex-wrap (2026-08-25): a cluster wider than a narrow
            tile WRAPS to a second row inside the card instead of escaping
            its right edge (shrink-0 overflowed; min-w-0 alone let the
            non-shrinkable circle/pills spill out of the shrunken box). */}
        <span className="flex min-w-0 flex-wrap items-center justify-end gap-1.5">
          {onCall ? (
            <span
              title={`On call now${run.activeCall.byName ? ` · ${run.activeCall.byName}` : ''}`}
              className={`inline-flex items-center gap-1 rounded-full px-1.5 py-px text-[10px] font-semibold ${
                solid
                  ? 'bg-white'
                  : 'bg-emerald-50 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-400'
              }`}
              // Solid mode: white pill, text in the TILE's own fill color —
              // pops on red, amber, and blue tiles alike.
              style={solid ? { color: fillFor(tileStripe) } : undefined}
            >
              <Phone size={10} className="animate-pulse" />
              On call
            </span>
          ) : null}
          {!onCall && awaitingLog ? (
            <span
              title="Call placed in the window, waiting on the outcome log. The clock is holding at the dial time."
              className={`inline-flex items-center gap-1 rounded-full px-1.5 py-px text-[10px] font-semibold ${
                solid
                  ? 'bg-white'
                  : 'bg-amber-50 text-amber-600 dark:bg-amber-500/15 dark:text-amber-400'
              }`}
              style={solid ? { color: fillFor(tileStripe) } : undefined}
            >
              <Phone size={10} />
              Log the call
            </span>
          ) : null}
          <Badges
            lead={run.lead}
            reachOuts={run.reachOuts}
            fill={fillFor(tileStripe)}
            booking={run.booking}
            observedAt={run.state === 'active' ? run.contactObservedAt : null}
          />
        </span>
      </div>
      {quietDay ? (
        <div className={`mt-1 truncate text-[11px] font-medium ${mutedText}`}>
          Breather
          {step ? ` · next d${step.day}` : ''}
        </div>
      ) : dayComplete ? (
        <div className="mt-1 flex items-center justify-between gap-1 text-[11px]">
          <span
            className={`font-medium ${
              solid ? 'text-white' : 'text-emerald-600 dark:text-emerald-400'
            }`}
          >
            Done for today
          </span>
          {step ? (
            <span className={`truncate ${mutedText}`}>
              Next: {nextStepLabel(step, run, now)}
            </span>
          ) : null}
        </div>
      ) : step ? (
        <div className="mt-1 flex items-center justify-between gap-1 text-[11px]">
          <span
            className={`inline-flex min-w-0 items-center gap-1 font-medium ${
              solid
                ? 'text-white'
                : tone.state === 'automated'
                  ? 'text-violet-600 dark:text-violet-400'
                  : tone.state === 'overdue'
                    ? 'text-red-500 dark:text-red-400'
                    : tone.state === 'due'
                      ? 'text-amber-600 dark:text-amber-400'
                      : 'text-slate-500 dark:text-slate-400'
            }`}
          >
            {tone.state === 'automated' ? (
              <Zap size={11} className="shrink-0" />
            ) : (
              <CalendarClock size={11} className="shrink-0" />
            )}
            <span className="truncate">{step.label || step.stepKey}</span>
          </span>
          <span
            title={
              paused
                ? `Away (${paused === 'meeting' ? 'a meeting' : paused === 'away' ? 'a break' : paused === 'call' ? 'a call' : 'lunch'}): the clock is paused and resumes after, with this much time added to the due time.`
                : creditMin > 0
                  ? `The due time moved ${creditMin}m for lunch or a meeting.`
                  : undefined
            }
            className={`min-w-0 truncate ${
              solid
                ? tone.state === 'due' || tone.state === 'overdue'
                  ? 'font-semibold text-white'
                  : 'text-white/75'
                : tone.state === 'automated'
                  ? 'text-violet-500 dark:text-violet-400'
                  : tone.state === 'due'
                    ? 'font-semibold text-amber-600 dark:text-amber-400'
                    : tone.state === 'overdue'
                      ? 'font-semibold text-red-500 dark:text-red-400'
                      : heldNew
                        ? 'font-semibold text-sky-600 dark:text-sky-300'
                        : 'text-slate-400 dark:text-slate-500'
            }`}
          >
            {step.status === 'missed' ? (
              'missed'
            ) : heldNew && ownerAwayNow ? (
              // Waiting on an owner who is away: just "paused" until they're
              // back (Nic 2026-10-21), then its 3 minutes start.
              <span className="inline-flex items-center gap-1">
                <AwayGlyph kind={ownerAwayNow.kind || 'lunch'} />
                {pausedLabelOf(ownerAwayNow.kind || 'lunch')}
              </span>
            ) : paused ? (
              <span className="inline-flex items-center gap-1">
                <AwayGlyph kind={paused} />
                away{creditMin > 0 ? ` +${creditMin}m` : ''}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1">
                {lockedNew ? (
                  <Lock
                    size={10}
                    className="shrink-0"
                    aria-label="Locked until it starts"
                  />
                ) : null}
                {creditMin > 0 ? (
                  <>
                    <AwayGlyph />
                    {`+${creditMin}m · `}
                  </>
                ) : null}
                {tone.state === 'upcoming' ||
                (tone.state === 'automated' &&
                  step.day > Math.min(run.currentDay, 10))
                  ? nextStepLabel(clockStep, run, now)
                  : stepCountdownLabel(clockStep, tone, now, workCalendar)}
              </span>
            )}
          </span>
        </div>
      ) : (
        <div
          className={`mt-1 text-[11px] font-medium ${
            solid ? 'text-white' : 'text-emerald-600 dark:text-emerald-400'
          }`}
        >
          Done for today
        </div>
      )}
      {/* Whose lead, bottom right, only for someone who sees everyone's
          (Nic 2026-10-23): on a rep's own board it was always their own. */}
      <div className="flex items-end justify-between gap-1">
        <StepChecks run={run} fill={fillFor(tileStripe)} />
        {showOwner && run.ownerInitials ? (
          <span
            title={run.ownerName || ''}
            className={`ml-auto mt-1.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[9px] font-bold ${
              solid
                ? 'bg-white/25 text-white'
                : 'bg-slate-100 text-slate-500 dark:bg-white/10 dark:text-slate-300'
            }`}
          >
            {run.ownerInitials}
          </span>
        ) : null}
      </div>
      {inWindow ? <BurnDownBar pct={tone.pct || 0} /> : null}
    </TileShell>
  );
};
